/**
 * Display names for the two-level outlet segmentation.
 *
 * `segment_type` is the parent level and reaches the frontend as the raw ClickHouse
 * value, which reads as a category list rather than a name. `sub_segment` is the level
 * beneath it and already carries presentable labels, so it needs no map.
 *
 * A value missing from this map renders as itself.
 */
const SEGMENT_LABEL = {
  restaurant: "Restaurant",
  "grocery/convenience store/supermarket/liquor store": "Non Restaurant",
};

/** Display name for a `segment_type`, falling back to the raw value. */
export const segmentLabel = (value) => SEGMENT_LABEL[value] ?? value;

/**
 * The child value standing for a parent's outlets that carry no sub-segment.
 *
 * Those rows hold NULL, which no `IN` list can match, so the API gives them their
 * own `IS NULL` clause keyed on this sentinel. It must match `UNSET_SUB_SEGMENT`
 * in `execution_view_models.py`.
 */
export const UNSET_SUB_SEGMENT = "__unset__";

/** Display name for a `sub_segment`, naming the unset bucket in the reader's terms. */
export const subSegmentLabel = (value) =>
  value === UNSET_SUB_SEGMENT ? "Not specified" : value;

/**
 * Encode one node of the segment tree as a stable key.
 *
 * A node is a parent plus an optional child, and the tree widget identifies nodes by a
 * single string. Both levels are free text from the segmenter and already contain
 * slashes, commas and parentheses, so any delimiter would need its own escaping. JSON
 * supplies that escaping, and round-trips through `decodeSegmentNode` unchanged.
 */
export const encodeSegmentNode = (segmentType, subSegment = null) =>
  JSON.stringify([segmentType, subSegment]);

/**
 * Decode a key from `encodeSegmentNode` into the filter node the API expects.
 *
 * A node with no child omits `sub_segment` entirely, which the API reads as the whole
 * parent rather than as a child named null.
 */
export const decodeSegmentNode = (key) => {
  const [segment_type, sub_segment] = JSON.parse(key);
  return sub_segment == null ? { segment_type } : { segment_type, sub_segment };
};

/**
 * Reduce a tree's checked keys to the smallest set of filter nodes meaning the same.
 *
 * A checkable tree reports every checked node, so a fully-checked parent arrives
 * alongside all of its children. The parent on its own already selects those children,
 * and it also selects the outlets beneath it carrying no sub-segment, so the children
 * add nothing. A partially-checked parent is not reported as checked, so its selected
 * children survive.
 *
 * A key that does not parse is skipped rather than thrown on, so one unreadable entry
 * costs its own node instead of the whole filter.
 */
export const segmentNodesToFilters = (keys) => {
  const nodes = [];
  for (const key of keys ?? []) {
    try {
      nodes.push(decodeSegmentNode(key));
    } catch {
      // Unreadable key: drop it rather than fail the whole filter.
    }
  }

  const wholeParents = new Set(
    nodes.filter((n) => n.sub_segment === undefined).map((n) => n.segment_type)
  );
  return nodes.filter(
    (n) => n.sub_segment === undefined || !wholeParents.has(n.segment_type)
  );
};
